import React, { useState, useEffect } from 'react';
import { Box, Text, keyframes, Icon, HStack, VStack, Collapse, useDisclosure } from '@chakra-ui/react';
import { BrainCircuit, Sparkles, ChevronDown, ChevronUp, FileText, Search, Database } from 'lucide-react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Keyframe animations
const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.1); }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(5px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const ThinkingDisplay = ({isThinking = true, live = false, onCitationClick, socketGeneration, isCollapsed = false,
                                    keepAfterDone = false, historyKey = 'global', }) => {
    const STORAGE_KEY = `thinking:${historyKey}`;
    const [thoughtHistory, setThoughtHistory] = useState(() => {
           try {
                 const saved = sessionStorage.getItem(STORAGE_KEY);
                 return saved ? JSON.parse(saved) : [];
               } catch {
                 return [];
               }
         });

    useEffect(() => {
              try {
                    const saved = sessionStorage.getItem(STORAGE_KEY);
                    setThoughtHistory(saved ? JSON.parse(saved) : []);
                  } catch {
                    setThoughtHistory([]);
                  }
          }, [STORAGE_KEY]);

     // persist on every change
         useEffect(() => {
             sessionStorage.setItem(STORAGE_KEY, JSON.stringify(thoughtHistory));
             }, [thoughtHistory, STORAGE_KEY]);
    const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: isCollapsed });
    const [isNewThought, setIsNewThought] = useState(false);
    const [currentThought, setCurrentThought] = useState("");

    // Add socket listener for thinking messages
    useEffect(() => {
        if (!socketGeneration || !live) return;

        const handleThinkingMessage = (data) => {
            console.log("LOGGING THINKING TRACE:", data.message);
            setCurrentThought(data.message);
        };

        socketGeneration.on('receive_message_thinking', handleThinkingMessage);

        return () => {
            socketGeneration.off('receive_message_thinking', handleThinkingMessage);
        };
    }, [socketGeneration, live]);

    useEffect(() => {
        console.log("Current thought updated:", currentThought)

        if (currentThought && currentThought.trim() !== "") {
            setThoughtHistory(prev => {
                // Only add if it's actually different from the last thought
                if (prev.length === 0 || prev[prev.length - 1] !== currentThought) {
                    setIsNewThought(true);
                    setTimeout(() => setIsNewThought(false), 500);
                    return [...prev.slice(-4), currentThought]; // Keep last 5 thoughts
                }
                return prev;
            });
        }
    }, [currentThought]);

    // Clear history when thinking stops
    // useEffect(() => {
    //     if (!isThinking) {
    //         const timer = setTimeout(() => {
    //             setThoughtHistory([]);
    //         }, 2000);
    //         return () => clearTimeout(timer);
    //     }
    // }, [isThinking]);

    const shouldHide = !isThinking && thoughtHistory.length === 0 && !keepAfterDone;
    if (shouldHide) return null;

    // Custom markdown components for thinking display
    const thinkingMarkdownComponents = {
        p: ({ children }) => {
            const childrenArray = React.Children.toArray(children);

            const processText = (text) => {
                if (typeof text !== 'string') return text;

                // Handle citations
                const parts = text.split(/(\[.*?\.(?:pdf|csv|docx),\s*p\.\s*\d+\])/g);
                return parts.map((part, index) => {
                    const citationMatch = part.match(/\[(.*?)\.(pdf|csv|docx),\s*p\.\s*(\d+)\]/);
                    if (citationMatch) {
                        const [_, filename, extension, page] = citationMatch;
                        return (
                            <Text
                                key={index}
                                as="span"
                                color="blue.500"
                                fontSize="xs"
                                cursor="pointer"
                                _hover={{ textDecoration: 'underline' }}
                                onClick={() => onCitationClick && onCitationClick(`${filename}.${extension}`, page)}
                            >
                                [{filename}.{extension}, p. {page}]
                            </Text>
                        );
                    }
                    return part;
                });
            };

            const content = childrenArray.map(child => {
                if (typeof child === 'string') {
                    return processText(child);
                }
                return child;
            });

            return <Text fontSize="sm" color="gray.700" mb={1}>{content}</Text>;
        },
        strong: ({ children }) => (
            <Text as="span" fontWeight="bold" color="gray.900">{children}</Text>
        ),
    };

    const getActivityIcon = () => {
        const latestThought = thoughtHistory[thoughtHistory.length - 1] || "";
        if (latestThought.toLowerCase().includes('comparing') || latestThought.toLowerCase().includes('comparative')) {
            return FileText;
        }
        if (latestThought.toLowerCase().includes('searching') || latestThought.toLowerCase().includes('analyzing')) {
            return Search;
        }
        if (latestThought.toLowerCase().includes('database') || latestThought.toLowerCase().includes('data')) {
            return Database;
        }
        return Sparkles;
    };

    const ActivityIcon = getActivityIcon();

    return (
        <Box
            bg="blue.50"
            borderRadius="lg"
            p={4}
            position="relative"
            overflow="hidden"
            border="1px solid"
            borderColor="blue.200"
            boxShadow="0 2px 12px rgba(59, 130, 246, 0.08)"
            animation={`${fadeIn} 0.3s ease-out`}
            width={'97%'}
            _before={{
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '2px',
                background: 'linear-gradient(90deg, transparent, #3182ce, transparent)',
                backgroundSize: '200% 100%',
                animation: isThinking ? `${shimmer} 2s ease-in-out infinite` : 'none',
            }}
        >
            <VStack spacing={3} align="stretch">
                {/* Header */}
                <HStack justify="space-between" align="center">
                    <HStack spacing={2}>
                        <Icon
                            as={BrainCircuit}
                            boxSize={5}
                            color="blue.600"
                            animation={isThinking ? `${pulse} 2s ease-in-out infinite` : 'none'}
                        />
                        <Text fontSize="sm" fontWeight="600" color="blue.700">
                            {isThinking ? "Thinking about your files..." : "Analysis complete"}
                        </Text>
                        <Icon
                            as={ActivityIcon}
                            boxSize={4}
                            color="blue.500"
                            opacity={0.7}
                        />
                    </HStack>

                    {thoughtHistory.length > 1 && (
                        <Icon
                            as={isOpen ? ChevronUp : ChevronDown}
                            boxSize={5}
                            color="blue.600"
                            cursor="pointer"
                            onClick={onToggle}
                            _hover={{ color: 'blue.700' }}
                        />
                    )}
                </HStack>

                {/* Current/Latest Thought */}
                {thoughtHistory.length > 0 ? (
                    <Box
                        bg="white"
                        p={3}
                        borderRadius="md"
                        border="1px solid"
                        borderColor="blue.100"
                        animation={isNewThought ? `${fadeIn} 0.3s ease-out` : 'none'}
                    >
                        <Markdown
                            remarkPlugins={[remarkGfm]}
                            components={thinkingMarkdownComponents}
                        >
                            {thoughtHistory[thoughtHistory.length - 1]}
                        </Markdown>
                    </Box>
                ) : null}

                {/* Thinking History (collapsible) */}
                {thoughtHistory.length > 1 && (
                    <Collapse in={isOpen} animateOpacity>
                        <VStack spacing={2} align="stretch" opacity={0.7}>
                            <Text fontSize="xs" color="gray.600" fontWeight="500">
                                Previous steps:
                            </Text>
                            {thoughtHistory.slice(0, -1).reverse().map((thought, index) => (
                                <Box
                                    key={index}
                                    bg="white"
                                    p={2}
                                    borderRadius="md"
                                    border="1px solid"
                                    borderColor="gray.200"
                                    opacity={1 - (index * 0.2)}
                                    fontSize="xs"
                                >
                                    <Markdown
                                        remarkPlugins={[remarkGfm]}
                                        components={thinkingMarkdownComponents}
                                    >
                                        {thought}
                                    </Markdown>
                                </Box>
                            ))}
                        </VStack>
                    </Collapse>
                )}

                {/* Progress indicator */}
                {isThinking && (
                    <HStack spacing={1} justify="center" pt={1}>
                        {[0, 1, 2].map((i) => (
                            <Box
                                key={i}
                                w="6px"
                                h="6px"
                                bg="blue.400"
                                borderRadius="full"
                                animation={`${pulse} 1.4s ease-in-out infinite`}
                                style={{ animationDelay: `${i * 0.2}s` }}
                            />
                        ))}
                    </HStack>
                )}
            </VStack>
        </Box>
    );
};
